import { createSelector } from 'reselect';
import { get, identity } from 'lodash/fp';
import { ProductPricingMode } from '@mews/enums';
import { ApplicationState } from '@mews-commander/types';
import { createOptionsSelector } from '@mews-commander/utils';
import { getChains } from '@mews-commander/legacy-entities/src/ducks/chains';
import {
    getUnusedSubscriptions,
    getSubscriptionIdByCode,
} from '@mews-commander/legacy-entities/src/ducks/subscriptions';
import { isNewChain } from '../utils';
import {
    DEFAULT_ENTERPRISE_INDEX,
    DEFAULT_HISTORY_WINDOW_JSON,
    DEFAULT_CHAIN_INDEX,
    enterpriseBuilderMetadata,
} from '../constants';
import { moduleSelector } from '../module';
import { isDemo, isDevelopment, isProduction } from '../../app/reducers/configuration';

export const getEnterpriseSettings = createSelector(moduleSelector, get('settingsStep'));

export const getEnterpriseSettingsWithDefaults = createSelector(getEnterpriseSettings, (enterpriseSettings) => {
    const { pricingMode } = enterpriseSettings;

    return {
        ...enterpriseSettings,
        pricingMode: pricingMode || ProductPricingMode.Gross,
    };
});

export const getSubscriptionOptions = createOptionsSelector(getUnusedSubscriptions, { value: 'code', label: 'code' });

export const getChainOptions = createOptionsSelector(getChains, { value: 'id', label: 'name' });

export const getSettingsExportData = createSelector(
    getEnterpriseSettings,
    identity,
    (settings, state: ApplicationState) => {
        let metadata: string;

        if (isDevelopment()) {
            metadata = enterpriseBuilderMetadata.development;
        } else if (isDemo()) {
            metadata = enterpriseBuilderMetadata.demo;
        } else if (isProduction()) {
            metadata = enterpriseBuilderMetadata.production;
        } else {
            metadata = enterpriseBuilderMetadata.unknown;
        }

        const {
            subscription,
            chain,
            newChainName,
            legalEmail,
            legalEnvironment,
            currency,
            culture,
            language,
            addressLine1,
            addressLine2,
            city,
            postalCode,
            countryCode,
            timeZone,
            pricingMode,

            // omitted props
            id,
            addressAutoComplete,

            ...other
        } = settings;

        const subscriptionId = getSubscriptionIdByCode(state, { code: subscription });

        const chains = isNewChain(chain) ? [{ name: newChainName, legalEmail, index: DEFAULT_CHAIN_INDEX }] : [];

        const chainAttribute = isNewChain(chain)
            ? {
                  chainIndex: DEFAULT_CHAIN_INDEX,
              }
            : {
                  chainId: chain,
              };

        return {
            chains,
            enterprises: [
                {
                    ...chainAttribute,
                    subscriptionId,
                    legalEnvironmentCode: legalEnvironment,
                    defaultCurrencyCode: currency,
                    defaultCultureCode: culture,
                    defaultLanguageCode: language,
                    timeZoneId: timeZone,
                    pricing: pricingMode,
                    address: {
                        addressLine1,
                        addressLine2,
                        city,
                        postalCode,
                        countryCode,
                    },
                    index: DEFAULT_ENTERPRISE_INDEX,
                    accountingEditableHistoryWindow: DEFAULT_HISTORY_WINDOW_JSON,
                    operationalEditableHistoryWindow: DEFAULT_HISTORY_WINDOW_JSON,
                    ...other,
                },
            ],
            metadata,
        };
    }
);
